import { useState } from "react";
import { trpc } from "../../lib/trpc.js";
import { baixar } from "../../lib/api.js";
import { DivulgacoesDaVivencia } from "./DivulgacoesDaVivencia.js";
import { Lista, Pagina } from "../../components/Pagina.js";
import {
  Botao,
  Campo,
  Entrada,
  Falha,
  Girando,
  Selecao,
  Selo,
  Vazio,
} from "../../components/ui.js";

/** Data curta, sem hora — nas listas administrativas a hora é ruído. */
function data(d: Date | string): string {
  return new Date(d).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function InstituicoesPage() {
  const [criando, setCriando] = useState(false);
  const [nome, setNome] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  const lista = trpc.institutions.list.useQuery();
  const utils = trpc.useUtils();

  const criar = trpc.institutions.create.useMutation({
    onSuccess: () => {
      void utils.institutions.list.invalidate();
      setCriando(false);
      setNome("");
      setErro(null);
    },
    onError: (e) => setErro(e.message),
  });

  const alternar = trpc.institutions.toggleStatus.useMutation({
    onSuccess: () => utils.institutions.list.invalidate(),
  });

  const itens = lista.data ?? [];

  return (
    <Pagina
      titulo="Locais/Eventos"
      descricao="Onde as vivências acontecem."
      acao={
        <Botao tom="brasa" onClick={() => setCriando((v) => !v)}>
          {criando ? "Cancelar" : "Novo local"}
        </Botao>
      }
    >
      {criando && (
        <div className="mb-5 flex flex-col gap-3 rounded-peca border border-borda bg-superficie p-4">
          <Campo rotulo="Nome">
            <Entrada value={nome} onChange={(e) => setNome(e.target.value)} autoFocus />
          </Campo>
          {erro && <Falha mensagem={erro} />}
          <Botao
            tom="brasa"
            carregando={criar.isPending}
            onClick={() => criar.mutate({ name: nome.trim() })}
          >
            Criar
          </Botao>
        </div>
      )}

      {lista.isLoading ? (
        <Girando className="mx-auto my-12 size-6 text-texto-fraco" />
      ) : itens.length === 0 ? (
        <Vazio
          titulo="Nenhum local cadastrado"
          descricao="Cadastre o primeiro local para poder criar vivências."
          acao={
            <Botao tom="brasa" onClick={() => setCriando(true)}>
              Novo local
            </Botao>
          }
        />
      ) : (
        <Lista
          itens={itens}
          chave={(i) => i.id}
          colunas={["Nome", "Contato", "Situação", ""]}
          cartao={(i) => (
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-medium">{i.name}</p>
                {i.contactName && (
                  <p className="truncate text-[0.8125rem] text-texto-suave">{i.contactName}</p>
                )}
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <Selo tom={i.status === "active" ? "fixado" : "quieto"}>
                  {i.status === "active" ? "ativa" : "inativa"}
                </Selo>
              </div>
            </div>
          )}
          linha={(i) => [
            i.name,
            i.contactName ?? "—",
            <Selo key="s" tom={i.status === "active" ? "fixado" : "quieto"}>
              {i.status === "active" ? "ativa" : "inativa"}
            </Selo>,
            <Botao
              key="a"
              tom="fantasma"
              formato="nu"
              onClick={() => alternar.mutate({ id: i.id })}
            >
              {i.status === "active" ? "Desativar" : "Ativar"}
            </Botao>,
          ]}
        />
      )}
    </Pagina>
  );
}

export function VivenciasPage() {
  const [criando, setCriando] = useState(false);
  // `undefined` = painel fechado; `null` = a lista do modo "Todas as vivências";
  // string = uma vivência. Sem os três estados, "fechado" e "a lista global"
  // seriam a mesma coisa.
  const [divulgando, setDivulgando] = useState<string | null | undefined>(undefined);
  const [nome, setNome] = useState("");
  const [instituicaoId, setInstituicaoId] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  const lista = trpc.vivencias.list.useQuery();
  const instituicoes = trpc.institutions.list.useQuery();
  const utils = trpc.useUtils();

  const criar = trpc.vivencias.create.useMutation({
    onSuccess: () => {
      void utils.vivencias.list.invalidate();
      setCriando(false);
      setNome("");
      setErro(null);
    },
    onError: (e) => setErro(e.message),
  });

  const alternar = trpc.vivencias.toggleStatus.useMutation({
    onSuccess: () => utils.vivencias.list.invalidate(),
  });

  const itens = lista.data ?? [];

  return (
    <Pagina
      titulo="Vivências"
      descricao="Cada evento. As fotos e a galeria são filtradas por aqui."
      acao={
        <Botao tom="brasa" onClick={() => setCriando((v) => !v)}>
          {criando ? "Cancelar" : "Nova vivência"}
        </Botao>
      }
    >
      {criando && (
        <div className="mb-5 flex flex-col gap-3 rounded-peca border border-borda bg-superficie p-4">
          <Campo rotulo="Nome">
            <Entrada value={nome} onChange={(e) => setNome(e.target.value)} autoFocus />
          </Campo>
          <Campo rotulo="Local/Evento">
            <Selecao value={instituicaoId} onChange={(e) => setInstituicaoId(e.target.value)}>
              <option value="">Selecione</option>
              {instituicoes.data?.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </Selecao>
          </Campo>
          {erro && <Falha mensagem={erro} />}
          <Botao
            tom="brasa"
            disabled={!instituicaoId || nome.trim().length < 2}
            carregando={criar.isPending}
            onClick={() => criar.mutate({ name: nome.trim(), institutionId: instituicaoId })}
          >
            Criar
          </Botao>
        </div>
      )}

      {/* A galeria também roda sem vivência escolhida, e nesse modo tem lista
          própria. Sem esta entrada, a única forma de mexer nela seria pelo banco. */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-peca border border-borda bg-superficie-alta px-4 py-3">
        <div>
          <p className="text-[0.9375rem] text-texto">Todas as vivências</p>
          <p className="text-[0.8125rem] text-texto-suave">
            O que passa na galeria quando nenhum evento é escolhido.
          </p>
        </div>
        <Botao
          tom="fantasma"
          formato="nu"
          onClick={() => setDivulgando((d) => (d === null ? undefined : null))}
        >
          Divulgações
        </Botao>
      </div>

      {divulgando !== undefined && (
        <div className="mb-5 rounded-peca border border-borda bg-superficie p-4">
          <p className="mb-3 font-display text-[1.125rem] leading-tight">
            {divulgando === null
              ? "Divulgações de Todas as vivências"
              : `Divulgações de ${itens.find((v) => v.id === divulgando)?.name}`}
          </p>
          <DivulgacoesDaVivencia
            vivenciaId={divulgando}
            aoFechar={() => setDivulgando(undefined)}
          />
        </div>
      )}

      {lista.isLoading ? (
        <Girando className="mx-auto my-12 size-6 text-texto-fraco" />
      ) : itens.length === 0 ? (
        <Vazio
          titulo="Nenhuma vivência"
          descricao="Crie a primeira para começar a atender. Ela precisa de um local."
        />
      ) : (
        <Lista
          itens={itens}
          chave={(v) => v.id}
          colunas={["Vivência", "Local/Evento", "Criada", "Situação", ""]}
          cartao={(v) => (
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-medium">{v.name}</p>
                <p className="truncate text-[0.8125rem] text-texto-suave">{v.institutionName}</p>
                <p className="mt-0.5 text-[0.75rem] text-texto-fraco">{data(v.createdAt)}</p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                <Selo tom={v.status === "active" ? "fixado" : "quieto"}>
                  {v.status === "active" ? "ativa" : "inativa"}
                </Selo>
                {v.ownerUserId === null && <Selo tom="projecao">do sistema</Selo>}
                <Botao
                  tom="fantasma"
                  formato="nu"
                  onClick={() => setDivulgando((d) => (d === v.id ? undefined : v.id))}
                >
                  Divulgações
                </Botao>
              </div>
            </div>
          )}
          linha={(v) => [
            v.name,
            v.institutionName,
            data(v.createdAt),
            <span key="s" className="flex flex-wrap gap-1.5">
              <Selo tom={v.status === "active" ? "fixado" : "quieto"}>
                {v.status === "active" ? "ativa" : "inativa"}
              </Selo>
              {/* Compartilhada pelo sistema: dá para usar no evento, não para editar. */}
              {v.ownerUserId === null && <Selo tom="projecao">do sistema</Selo>}
            </span>,
            <div key="a" className="flex flex-wrap gap-3">
              <Botao
                tom="fantasma"
                formato="nu"
                onClick={() => setDivulgando((d) => (d === v.id ? undefined : v.id))}
              >
                Divulgações
              </Botao>
              {/* Compartilhada é do sistema: oferecer "Desativar" daria um botão
                  que só devolve erro. A curadoria de divulgações continua
                  disponível — a lista é de quem usa a vivência, não dela. */}
              {v.ownerUserId !== null && (
                <Botao tom="fantasma" formato="nu" onClick={() => alternar.mutate({ id: v.id })}>
                  {v.status === "active" ? "Desativar" : "Ativar"}
                </Botao>
              )}
            </div>,
          ]}
        />
      )}
    </Pagina>
  );
}

/** Uma tela de lista comporta bem esta quantidade sem virar rolagem infinita. */
const PARTICIPANTES_POR_PAGINA = 50;

export function ParticipantesPage() {
  const [busca, setBusca] = useState("");
  const [instituicaoId, setInstituicaoId] = useState("");
  const [vivenciaId, setVivenciaId] = useState("");
  const [pagina, setPagina] = useState(0);

  const instituicoes = trpc.institutions.list.useQuery({});
  // As vivências acompanham a instituição escolhida, senão o operador monta
  // combinações que não existem e recebe uma lista vazia sem entender.
  const vivencias = trpc.vivencias.list.useQuery(
    instituicaoId ? { institutionId: instituicaoId } : undefined,
  );

  const lista = trpc.participants.list.useQuery({
    search: busca || undefined,
    institutionId: instituicaoId || undefined,
    vivenciaId: vivenciaId || undefined,
    limit: PARTICIPANTES_POR_PAGINA,
    offset: pagina * PARTICIPANTES_POR_PAGINA,
  });
  const itens = lista.data?.itens ?? [];
  const total = lista.data?.total ?? 0;
  const paginas = Math.ceil(total / PARTICIPANTES_POR_PAGINA);
  const filtrando = Boolean(busca || instituicaoId || vivenciaId);

  /** Trocar filtro invalida a página em que se estava. */
  function filtrar(mudanca: () => void) {
    mudanca();
    setPagina(0);
  }

  /** A planilha sai com o que está na tela: os mesmos filtros vão na URL. */
  function exportar(formato: "csv" | "xlsx") {
    const p = new URLSearchParams({ formato });
    if (busca) p.set("search", busca);
    if (instituicaoId) p.set("institutionId", instituicaoId);
    if (vivenciaId) p.set("vivenciaId", vivenciaId);
    baixar(`/api/participants/export?${p.toString()}`);
  }

  return (
    <Pagina
      titulo="Participantes"
      descricao={
        paginas > 1
          ? `${total} cadastrados — mostrando ${pagina * PARTICIPANTES_POR_PAGINA + 1} a ${Math.min((pagina + 1) * PARTICIPANTES_POR_PAGINA, total)}.`
          : `${total} cadastrados.`
      }
      acao={
        itens.length > 0 ? (
          <div className="flex gap-2">
            <Botao tom="quieto" onClick={() => exportar("csv")}>
              CSV
            </Botao>
            <Botao tom="brasa" onClick={() => exportar("xlsx")}>
              Excel
            </Botao>
          </div>
        ) : undefined
      }
    >
      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <Entrada
          type="search"
          placeholder="Nome ou WhatsApp"
          value={busca}
          onChange={(e) => filtrar(() => setBusca(e.target.value))}
        />
        <Selecao
          value={instituicaoId}
          onChange={(e) =>
            filtrar(() => {
              setInstituicaoId(e.target.value);
              setVivenciaId("");
            })
          }
        >
          <option value="">Todos os locais</option>
          {instituicoes.data?.map((i) => (
            <option key={i.id} value={i.id}>
              {i.name}
            </option>
          ))}
        </Selecao>
        <Selecao value={vivenciaId} onChange={(e) => filtrar(() => setVivenciaId(e.target.value))}>
          <option value="">Todas as vivências</option>
          {vivencias.data?.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </Selecao>
      </div>

      {lista.isLoading ? (
        <Girando className="mx-auto my-12 size-6 text-texto-fraco" />
      ) : itens.length === 0 ? (
        <Vazio
          titulo={filtrando ? "Ninguém com esses filtros" : "Nenhum participante"}
          descricao={
            filtrando
              ? "Tente parte do nome, os últimos dígitos do telefone, ou limpe os filtros."
              : "Os participantes são cadastrados durante o atendimento."
          }
        />
      ) : (
        <Lista
          itens={itens}
          chave={(p) => p.id}
          colunas={["Nome", "WhatsApp", "E-mail", "Desde"]}
          cartao={(p) => (
            <div>
              <p className="font-medium">
                {p.firstName} {p.lastName}
              </p>
              <p className="font-mono text-[0.8125rem] text-texto-suave">{p.whatsappE164}</p>
            </div>
          )}
          linha={(p) => [
            `${p.firstName} ${p.lastName}`,
            <span key="w" className="font-mono text-[0.875rem]">
              {p.whatsappE164}
            </span>,
            p.email ?? "—",
            data(p.createdAt),
          ]}
        />
      )}

      {paginas > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <Botao tom="quieto" disabled={pagina === 0} onClick={() => setPagina((n) => n - 1)}>
            Anterior
          </Botao>
          <span className="text-[0.8125rem] text-texto-fraco">
            {pagina + 1} de {paginas}
          </span>
          <Botao
            tom="quieto"
            disabled={pagina + 1 >= paginas}
            onClick={() => setPagina((n) => n + 1)}
          >
            Próxima
          </Botao>
        </div>
      )}
    </Pagina>
  );
}
